import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import Switch from "react-switch";

import {
  selectMatchingBoundingBoxes,
  selectAllBoundingBoxes,
  selectCurrentPage,
  selectActiveBoxes
} from "../../store/selector";

import { setImagesLoaded } from "../../store/action";
import theme from "../../theme";

const ShadowBox = styled.div`
  margin: 0 20px;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.14);

  @media (min-width: 1024px) {
    margin: 0;
  }
`;

const ImageResult = styled.div`
  @media (min-width: 1024px) {
    width: 50%;
  }
`;

const ImageBox = styled.div`
  position: relative;
`;

const Image = styled.img`
  display: block;
  max-width: 100%;
`;

const Rectangle = styled.div`
  position: absolute;
  background-color: rgba(24, 63, 92, 0.4);
  outline: ${(props) =>
    props.active ? `2px solid ${props.theme.palette.primary}` : undefined};
`;

const SwitchBox = styled.div`
  padding: 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (min-width: 1024px) {
    justify-content: flex-start;
    padding: 20px 0 0 0;
  }
`;

const SwitchLabel = styled.span`
  font-size: 14px;
  font-family: ${(props) => props.theme.typo.fontFamily};
  margin-right: 15px;
  color: #949494;
`;

export const ImageResultContainer = () => {
  const dispatch = useDispatch();
  const [xFactor, setXFactor] = useState(1);
  const [yFactor, setYFactor] = useState(1);
  const [showAllBoxes, setShowAllBoxes] = useState(true);
  const boundingBoxes = useSelector(selectMatchingBoundingBoxes);
  const allBoundingBoxes = useSelector(selectAllBoundingBoxes);
  const currentPage = useSelector(selectCurrentPage);
  const activeBoxes = useSelector(selectActiveBoxes);
  const imageElement = useRef(null);

  const handleImageLoad = () => {
    const { naturalWidth, naturalHeight, width, height } = imageElement.current;
    // TODO there is a bug in Safari that the width/height equal the
    //  naturalWidth/Height, whereas in most cases it should be smaller
    //  as a consequence the boxes aren't aligned with the image
    setXFactor(width / naturalWidth);
    setYFactor(height / naturalHeight);
    dispatch(setImagesLoaded(true));
  };

  const handleChange = (change) => setShowAllBoxes(change);

  useEffect(() => {
    const handleResize = () => {
      if (imageElement.current) {
        const {
          naturalWidth,
          naturalHeight,
          width,
          height
        } = imageElement.current;

        setXFactor(width / naturalWidth);
        setYFactor(height / naturalHeight);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <ImageResult>
      <ShadowBox>
        <ImageBox>
          <Image
            ref={imageElement}
            src={currentPage}
            alt="Processed image"
            onLoad={handleImageLoad}
          />

          {showAllBoxes &&
            allBoundingBoxes.map((box) => (
              <Rectangle
                key={`box-${box.id}`}
                id={`box-${box.id}`}
                active={activeBoxes.indexOf(box.id) > -1}
                style={{
                  top: box.y1 * yFactor,
                  left: box.x1 * xFactor,
                  width: (box.x2 - box.x1) * yFactor,
                  height: (box.y2 - box.y1) * xFactor
                }}
              />
            ))}

          {!showAllBoxes &&
            boundingBoxes.map((box) => (
              <Rectangle
                key={`box-${box.id}`}
                id={`box-${box.id}`}
                active={activeBoxes.indexOf(box.id) > -1}
                style={{
                  top: box.y1 * yFactor,
                  left: box.x1 * xFactor,
                  width: (box.x2 - box.x1) * yFactor,
                  height: (box.y2 - box.y1) * xFactor
                }}
              />
            ))}
        </ImageBox>
      </ShadowBox>
      <SwitchBox>
        <SwitchLabel>Show all</SwitchLabel>
        <Switch
          onColor={theme.palette.primary}
          onChange={handleChange}
          checked={showAllBoxes}
        />
      </SwitchBox>
    </ImageResult>
  );
};

export default ImageResultContainer;
